import { Amplitude } from 'services/analytics/amplitude';
import type { AnalyticsEvent } from 'services/analytics/events';
import { GtagAnalytics } from 'services/analytics/gtag';
import { FBPixel } from 'services/analytics/pixel';

import { getLocaleByPath } from 'utils/getLocaleByPath';

export const locales = ['en', 'hi'];

const defaultPaths = [
  'app/login',
  'app/account',
  'app/editor',
  'app/choose-plan',
  'app/payment',
  'app/payment-success',
  'app/merge-pdf',
  'app/merge-img',
  'app/split-pdf',
  'app/compress-pdf',
  'app/sign-pdf',
  'app/password-protect-pdf',
  'app/sign-up',
  'app/cancel-coupon',
  'app/cancel-quiz',
  'app/cancel-success',
  'app/pdf-converter',
  'app/create-pdf',
  'app/delete-pages-from-pdf',
  'app/rotate-pdf',
  'app/edit-pdf',
  'app/fill-pdf',
  'app/form-ds11',
  'app/form-i9',
  'app/form-w9',
  'app/form-w2',
  'app/form-w3',
  'app/form-941',
  'app/form-1099',
  'app/form-1099-misc',
  'app/form-1099-nec',
  'app/form-1098-t',
  'app/form-1095-a',
  'app/form-1065',
  'app/form-2553',
  'app/form-1040',
  'app/form-w4',
  'app/form-ds82',
  'app/form-cms-1500',
  'app/form-433-d',
  'app/form-1040-x',
  'app/form-4506-t',
  'app/form-i-864',
  'app/form-ss-5',
  'app/cv-builder',
  'app/special-offer',
  'app/pdf-ocr',
  'app/image-to-text',
  'app/translate-pdf',
  'app/login',
  'app/account',
  'app/editor',
  'app/choose-plan',
  'app/payment',
  'app/payment-success',
  'app/merge-pdf',
  'app/merge-img',
  'app/split-pdf',
  'app/compress-pdf',
  'app/sign-pdf',
  'app/password-protect-pdf',
  'app/sign-up',
  'app/cancel-coupon',
  'app/cancel-quiz',
  'app/cancel-success',
  'app/pdf-converter',
  'app/create-pdf',
  'app/delete-pages-from-pdf',
  'app/rotate-pdf',
  'app/edit-pdf',
  'app/fill-pdf',
  'app/form-ds11',
  'app/form-i9',
  'app/form-w9',
  'app/form-w2',
  'app/form-w3',
  'app/form-941',
  'app/form-1099',
  'app/form-1099-misc',
  'app/form-1099-nec',
  'app/form-1098-t',
  'app/form-1095-a',
  'app/form-1065',
  'app/form-2553',
  'app/form-1040',
  'app/form-w4',
  'app/form-ds82',
  'app/form-cms-1500',
  'app/form-433-d',
  'app/form-1040-x',
  'app/form-4506-t',
  'app/form-i-864',
  'app/form-ss-5',
  'app/cv-builder',
  'app/special-offer',
  'app/pdf-ocr',
  'app/image-to-text',
  'app/complete-merge',
  'app/complete-split',
  'app/translate-pdf',
  'app/complete-merge'
];

export const Analytics = {
  sendEvent: async (event: AnalyticsEvent) => {
    const mappedEvent = {
      ...event,
      data: { ...event.data, env: process.env.REACT_APP_PUBLIC_APP_ENV }
    };

    Amplitude.track(mappedEvent);
    FBPixel.track(mappedEvent);
    GtagAnalytics.track(mappedEvent);
  },
  updateUser: (data: AnalyticsUserProp | AnalyticsUserProp[]) => {
    Amplitude.updateUser(data);
    FBPixel.updateUser(data);
    GtagAnalytics.updateUser(data);
  },
  setUserId: (userId: string) => {
    Amplitude.setUserId(userId);
    FBPixel.setUserId(userId);
    GtagAnalytics.setUserId(userId);
  },
  getCurrentPageName: () => {
    const path = window.location.pathname?.replace('tools/', '');
    const woSlash = path.replace('/', '');

    if (woSlash.includes('delete')) {
      return woSlash;
    }

    const locale = getLocaleByPath(path);

    const woLocales = locale ? woSlash.replace(`/${locale}`, '') : woSlash;

    // converters
    if (path.includes('-to-')) {
      return woLocales.replace(/-/g, '_');
    }

    if (defaultPaths.includes(woLocales)) {
      return woLocales;
    }

    if (window.location.pathname.includes('reset-password')) {
      return 'restore';
    }

    switch (window.location.pathname?.replace('tools/', '')) {
      case '/app':
      case '/app/v1':
        return 'app/main_page';
      case '/app/tools/hi':
        return 'app/tools/main_page/hi';
      case '/app/tools/pt':
        return 'app/tools/main_page/pt';
      case '/app/tools/es':
        return 'app/tools/main_page/es';
      case '/app/tools/th':
        return 'app/tools/main_page/th';
      case '/app/tools/ar':
        return 'app/tools/main_page/ar';
      case '/app/tools/fr':
        return 'app/tools/main_page/fr';
      default:
        return '404';
    }
  },
  getCurrentPageLocale: () => {
    const path = window.location.pathname?.replace('tools/', '');
    const woSlash = path.replace('/', '');

    if (woSlash.includes('delete')) {
      return woSlash;
    }

    const locale = getLocaleByPath(path);

    const woLocales = locale ? woSlash.replace(`/${locale}`, '') : woSlash;

    // converters
    if (path.includes('-to-')) {
      return woSlash.replace(/-/g, '_');
    }

    if (defaultPaths.includes(woLocales)) {
      return woSlash;
    }

    if (window.location.pathname.includes('reset-password')) {
      return 'restore';
    }

    switch (window.location.pathname?.replace('tools/', '')) {
      case '/app':
      case '/app/v1':
        return 'app/main_page';
      case '/app/tools/hi':
        return 'app/tools/main_page/hi';
      case '/app/tools/pt':
        return 'app/tools/main_page/pt';
      case '/app/tools/es':
        return 'app/tools/main_page/es';
      case '/app/tools/th':
        return 'app/tools/main_page/th';
      case '/app/tools/ar':
        return 'app/tools/main_page/ar';
      case '/app/tools/fr':
        return 'app/tools/main_page/fr';
      default:
        return '404';
    }
  }
};

export interface AnalyticsService {
  init: () => void;
  track: (event: AnalyticsEvent) => void;
  updateUser: (data: AnalyticsUserProp | AnalyticsUserProp[]) => void;
  setUserId: (userId: string) => void;
}

export interface AnalyticsUserProp {
  key: string;
  value: string | number | boolean | string[];
}
